import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { ScopeOption } from '@/services/optionsService';

/** Dropdown multi-select con icono, checkboxes y buscador (filtros de scope). */
export function MultiSelect({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  searchable,
  fullWidth,
  allLabel,
}: {
  label: string;
  icon?: LucideIcon;
  options: ScopeOption[];
  selected: Array<number | string>;
  onChange: (ids: Array<number | string>) => void;
  searchable?: boolean;
  fullWidth?: boolean;
  /** Si se pasa, muestra una fila "Todas/os …" que representa el estado sin filtro
   *  (no agrega ningún valor al scope; al clickearla limpia la selección). */
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(
    () => (q ? options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())) : options),
    [options, q],
  );

  const toggle = (id: number | string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const active = selected.length > 0;
  // "Todas": tildada solo cuando TODAS las opciones están elegidas; al clickearla marca
  // todas (o las limpia si ya estaban todas). Por default (nada elegido) queda destildada.
  const allSelected = options.length > 0 && selected.length >= options.length;
  // 1 seleccionado → su nombre; varios → "Label (N)"; ninguno → "Label".
  const oneName = selected.length === 1 ? options.find((o) => o.id === selected[0])?.name : undefined;
  const buttonText = oneName ?? `${label}${active ? ` (${selected.length})` : ''}`;

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={buttonText}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition ${
          fullWidth ? 'w-full justify-between' : ''
        } ${active ? 'border-red-600 font-semibold text-red-700' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className={`truncate ${fullWidth ? 'min-w-0 flex-1 text-left' : 'max-w-[150px]'}`}>{buttonText}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          {searchable && (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="mb-1.5 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-red-500"
            />
          )}
          <div className="max-h-56 overflow-auto">
            {allLabel && !q && (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <input
                  type="checkbox"
                  className="accent-red-600"
                  checked={allSelected}
                  onChange={() => onChange(allSelected ? [] : options.map((o) => o.id))}
                />
                <span className="truncate">{allLabel}</span>
              </label>
            )}
            {filtered.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">Sin opciones</p>}
            {filtered.map((o) => (
              <label
                key={o.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 text-sm hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  className="accent-red-600"
                  checked={selected.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span className="truncate">{o.name}</span>
              </label>
            ))}
          </div>
          {active && (
            <button
              onClick={() => onChange([])}
              className="mt-1.5 w-full rounded-lg bg-slate-50 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              Limpiar ({selected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}
